/**
 * Fetch helper that automatically includes authentication headers
 * This is needed for streaming requests that use fetch instead of axios
 */

import { getAccessToken } from '../../utils/cookie.utils';
import { isWebBuild } from '../../utils/environment';
import { getEncryptedToken } from '../interceptors/jwt.interceptor';

/**
 * Adds authentication headers to fetch requests
 * Mimics the behavior of the axios request interceptor
 */
export function getAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = {};
  const isWeb = isWebBuild();

  // Get access token
  let accessToken = typeof window !== 'undefined' ? (window as any)['appAccessToken'] : null;
  if (isWeb && !accessToken) {
    accessToken = getAccessToken();
  }

  // JWT in Authorization Bearer header
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Add app key if available
  const encrypted = getEncryptedToken();
  if (encrypted) {
    headers['app-key'] = `${encrypted}`;
  }

  return headers;
}

/**
 * Fetch wrapper that automatically includes authentication headers
 * Use this instead of native fetch for API calls
 */
export async function fetchWithAuth(
  url: string | URL | Request,
  init?: RequestInit
): Promise<Response> {
  const authHeaders = getAuthHeaders();
  
  // Merge auth headers with existing headers
  const headers = new Headers(init?.headers || {});
  Object.entries(authHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  return fetch(url, {
    ...init,
    headers,
  });
}
